<template>
  <div class="h-full w-full flex flex-col">
    <div class="h-full container mx-auto">
      <div class="px-8 flex flex-col h-full w-full py-8">
        <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
          <div class="flex items-center text-sm font-light">
            <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
            <span class="mx-2">/</span>
            <router-link to="/employment" class="opacity-50 hover:opacity-100">Employment</router-link>
            <span class="mx-2">/</span>
            <router-link to="/jobs">Jobs</router-link>
          </div>
          <div class="font-bold">Jobs</div>
        </div>

        <div v-if="role == 'admin' || role == 'superadmin' || role == 'careercounselor'"
          class="mt-8 w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
          <h1 class="text-base md:text-lg font-semibold">Statistics</h1>
          <div class="flex flex-row flex-wrap">
            <div class="w-full sm:w-1/2 xl:w-1/3 2xl:w-1/4 flex flex-col text-base md:text-lg pt-4">
              <div class="flex justify-between sm:justify-start items-center flex-wrap">
                <div class="w-auto sm:w-64">Total Jobs</div>
                <div class="flex items-center">
                  <span class="hidden sm:block mr-0 sm:mr-2">:</span><span class="font-bold">{{
                    !$apollo.queries.jobs.loading ? paginatorInfo.total : 0
                  }}</span>
                </div>
              </div>
              <div class="flex justify-between sm:justify-start items-center flex-wrap mt-2">
                <div class="w-auto sm:w-64">Total Vacancies</div>
                <div class="flex items-center">
                  <span class="hidden sm:block mr-0 sm:mr-2">:</span><span class="font-bold">{{
                    jobReport ? jobReport.total_vacancies : 0
                  }}</span>
                </div>
              </div>
              <div class="flex justify-between sm:justify-start items-center flex-wrap mt-2">
                <div class="w-auto sm:w-64">Total Job Applications</div>
                <div class="flex items-center">
                  <span class="hidden sm:block mr-0 sm:mr-2">:</span><span class="font-bold">{{
                    jobReport ? jobReport.total_job_applications : 0
                  }}</span>
                </div>
              </div>
              <div class="flex justify-between sm:justify-start items-center flex-wrap mt-2">
                <div class="w-auto sm:w-64">Approved</div>
                <div class="flex items-center">
                  <span class="hidden sm:block mr-0 sm:mr-2">:</span><span class="font-bold">{{
                    jobReport ? jobReport.approved_count : 0
                  }}</span>
                </div>
              </div>
              <div class="flex justify-between sm:justify-start items-center flex-wrap mt-2">
                <div class="w-auto sm:w-64">Rejected</div>
                <div class="flex items-center">
                  <span class="hidden sm:block mr-0 sm:mr-2">:</span><span class="font-bold">{{
                    jobReport ? jobReport.rejected_count : 0
                  }}</span>
                </div>
              </div>
              <div class="flex justify-between sm:justify-start items-center flex-wrap mt-2">
                <div class="w-auto sm:w-64">Not Verified</div>
                <div class="flex items-center">
                  <span class="hidden sm:block mr-0 sm:mr-2">:</span><span class="font-bold">{{
                    jobReport ? jobReport.not_verified_yet_count : 0
                  }}</span>
                </div>
              </div>
            </div>
            <div class="w-full sm:w-1/2 xl:w-2/3 2xl:w-3/4 flex justify-center">
              <apexchart type="donut" width="380" :options="chartOptions" :series="series"></apexchart>
            </div>
          </div>
        </div>

        <div v-if="hasPermission('jobs-export-excel')" class="mt-4 flex justify-end items-center">
          <div v-if="isExportExcel"
            class="w-44 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg hover:text-ternary text-white">
            <base-spinner-with-text class-list="w-4 h-4" text="" text-class-list="text-white"></base-spinner-with-text>
            <span class="text-xs">Exporting Excel</span>
          </div>
          <div v-else @click="generateReport"
            class="w-44 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg cursor-pointer hover:text-ternary text-white">
            <font-awesome-icon :icon="['fas', 'file-excel']" />
            <span class="text-xs ml-2">Export Excel</span>
          </div>
        </div>

        <section :class="hasPermission('jobs-export-excel') ? 'mt-4' : 'mt-8'"
          class="h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
          <div class="flex justify-between flex-wrap gap-4">
            <h1 class="text-base md:text-lg font-semibold">Jobs List</h1>
            <div class="flex justify-start items-center flex-wrap gap-4">
              <div v-if="isShowFilters" class="w-full sm:w-auto border rounded-lg px-4 h-8 flex items-center">
                <div class="text-sm mr-2">Entity:</div>
                <select v-model="filterByEntity" class="outline-none text-sm flex-grow">
                  <option value="">All</option>
                  <option :value="item.entity" v-for="(item, index) in filteredEntities" :key="index">{{
                    getEntityName(item.entity) }}</option>
                </select>
              </div>
              <div v-if="isShowFilters" class="w-full sm:w-auto border rounded-lg px-4 h-8 flex items-center">
                <div class="text-sm mr-2">Employer Type:</div>
                <select v-model="filterByEmployerType" class="outline-none text-sm w-full sm:w-auto">
                  <option value="">All</option>
                  <option value="registered">Registered Employer</option>
                  <option value="non_registered">Non - Registered Employer</option>
                </select>
              </div>
              <div v-if="isShowFilters" class="w-full sm:w-auto border rounded-lg px-4 h-8 flex items-center">
                <div class="text-sm mr-2">Employer:</div>
                <select v-model="filterByPartner" class="outline-none text-sm w-full sm:w-auto">
                  <option value="">All Employers</option>
                  <option :value="item.id" v-for="(item, index) in partnersList" :key="index">{{ item.en_company_name
                    }}</option>
                </select>
              </div>
              <div v-if="isShowFilters" class="w-full sm:w-auto border rounded-lg px-4 h-8 flex items-center">
                <div class="text-sm mr-2">Sector:</div>
                <select v-model="filterBySector" class="outline-none text-sm flex-grow">
                  <option value="">All</option>
                  <option value="Government – Federal">Government – Federal</option>
                  <option value="Government – Local">Government – Local</option>
                  <option value="Semi-Government">Semi-Government</option>
                  <option value="Private">Private</option>
                </select>
              </div>
              <div class="w-full sm:w-auto border rounded-lg px-4 h-8 flex items-center">
                <div class="text-sm mr-2">Local or Non Local:</div>
                <select v-model="filterByJobFor" class="outline-none text-sm w-full sm:w-auto">
                  <option value="">All</option>
                  <option value="1">Local</option>
                  <option value="2">Non Local</option>
                  <option value="3">Both Local and Non Local</option>
                </select>
              </div>
              <div class="w-full sm:w-auto border rounded-lg px-4 h-8 flex items-center">
                <div class="text-sm mr-2">Applications:</div>
                <select v-model="filterByJobApplications" class="outline-none text-sm w-full sm:w-auto">
                  <option value="">All</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>
              <div class="w-full sm:w-auto">
                <search @on-search="onSearch" class-list="px-4 h-8"></search>
              </div>
              <div v-if="hasPermission('jobs-create')" @click.stop="createModal = true"
                class="flex items-center bg-ternary hover:bg-white border-ternary border px-4 py-2 rounded-lg cursor-pointer text-white hover:text-ternary">
                <icon-plus class="w-4 h-4"></icon-plus>
                <span class="text-xs ml-2">Add Job</span>
              </div>
            </div>
          </div>

          <div class="flex flex-col" v-if="$apollo.queries.jobs.loading">
            <table-shimmer :titleHeaders="[
              '#',
              'Entity',
              'Job Title',
              'Position',
              'Job Location',
              'Vacancy',
              'Applications',
              'Accept',
              'Salary',
              'Job Offer Start Date',
              'Job Offer Close Date',
              'Actions',
            ]" :rows="first"></table-shimmer>
          </div>
          <div v-else-if="!$apollo.queries.jobs.loading && !hasItems" class="h-full flex justify-center items-center">
            <h1 class="text-center text-xl font-medium">No jobs found!</h1>
          </div>
          <div v-else class="flex flex-col">
            <div class="flex flex-col overflow-x-auto">
              <div class="inline-block min-w-full py-2">
                <div class="overflow-x-auto">
                  <table class="min-w-full text-left text-sm font-light">
                    <thead class="border-b font-medium dark:border-neutral-500">
                      <tr>
                        <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                          #
                        </th>
                        <th v-if="role == 'admin' || role == 'superadmin'" scope="col" class="px-6 py-2 text-gray-400">
                          Employer
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Entity
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Job Title
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Position
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Job Location
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Vacancy
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Applications
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Accept
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Salary Range
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Job Offer Start Date
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Job Offer Close Date
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Status
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Posted By
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in items" :key="index" :class="item.is_posted ? '' : 'bg-yellow-300'"
                        class="border-b border-dashed dark:border-neutral-500">
                        <td class="whitespace-nowrap px-6 py-2 font-medium">
                          {{ paginatorInfo.firstItem + index }}
                        </td>
                        <td v-if="role == 'admin' || role == 'superadmin'" class="whitespace-nowrap px-6 py-2">
                          <div class="flex items-center">
                            <div v-if="item.partner" class="flex-shrink-0 mr-3">
                              <img class="h-12 w-12 rounded-lg object-contain cursor-pointer"
                                :src="getSrcFile(item.partner.company_logo)" alt="" />
                            </div>
                            <div class="flex-grow flex flex-col mr-4">
                              <h1 class="text-sm font-semibold flex items-center">
                                <span class="text-heading hover:text-blue-600 hover:underline cursor-pointer">{{
                                  item.partner ? item.partner.en_company_name : item.employer_name }}</span>
                              </h1>
                              <p v-if="item.partner" class="text-xs text-gray-400">
                                {{ item.partner.ar_company_name }}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ getEntityName(item.entity) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.job_title }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.position_name }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.job_location }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.vacancy }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <span v-if="item.request_count" @click.stop="viewApplications(item.token)"
                            class="hover:underline hover:text-blue-600 cursor-pointer"
                            title="Click to view the applications">{{ item.request_count }}</span>
                          <span v-else>{{ item.request_count }}</span>
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <span v-if="item.approved_count" @click.stop="showStudentsList(item.id, true)"
                            class="hover:underline hover:text-blue-600 cursor-pointer"
                            title="Click to view the students list">{{ item.approved_count }}</span>
                          <span v-else>{{ item.approved_count }}</span>
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.salary }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ formattedDate(item.job_start_date) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ formattedDate(item.job_close_date) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.is_posted ? 'Posted' : 'Not posted yet' }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.posted_by ? item.posted_by.name : item.created_by }}
                        </td>
                        <td class="px-6 py-2">
                          <div class="flex">
                            <div v-if="hasPermission('jobs-show')" @click.stop="viewItem(item)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                              title="View Job">
                              <icon-eye
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                            </div>
                            <div v-if="hasPermission('jobs-edit') && isEditableForMe(item.created_by_user_id)"
                              @click.stop="editItem(item)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                              title="Edit Job">
                              <icon-pen
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-pen>
                            </div>
                            <div v-if="hasPermission('jobs-delete')" @click.stop="deleteItem(item.id)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                              title="Delete Job">
                              <icon-trash
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-trash>
                            </div>
                            <div v-if="
                              hasPermission(
                                'job-request-and-approval-index'
                              ) && item.request_count
                            " @click.stop="viewApplications(item.token)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                              title="View Job Applications">
                              <icon-document-list
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-document-list>
                            </div>
                            <div v-if="hasPermission('jobs-send-email') && item.is_posted"
                              @click.stop="openEmailContentModal(item.id)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                              title="Send Job Opportunity Email">
                              <icon-envelope
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-envelope>
                            </div>
                            <div v-if="item.is_posted"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer">
                              <rsd-copy-to-clipboard
                                :text-to-copy="getJobOpportunityUrl(item.token)"></rsd-copy-to-clipboard>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
            </div>
          </div>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="createModal" :title="`Create Job`" @close="closeModal">
              <create :partners-list="partnersList" :non-registered-employers-list="nonRegisteredEmployersList"
                @created="onCreated" @close="closeModal"></create>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="editModal" :title="`Edit Job`" @close="closeModal">
              <edit @updated="onUpdated" @close="closeModal" :selected-item="selectedItem"></edit>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="isViewItem"
              :title="`View Job - ${selectedItem.job_title} (${selectedItem.employer_name})`" height=""
              @close="closeModal">
              <apply-job :item="selectedItem" user-type="partner" @close="closeModal"></apply-job>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-modal v-if="isShowJobAcceptedStudents" :title="`Job - #${selectedJobId} - Students List`" height=""
              @close="closeModal">
              <job-accepted-students :job-id="selectedJobId" :is-approved="isApproved"
                @close="closeModal"></job-accepted-students>
            </base-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-modal v-if="showAddNote" :title="`Add Your Note`" @close="closeAddNote">
              <div class="flex flex-col">
                <label for="note" class="px-8 mt-4 text-sm ml-1">Message</label>
                <div class="h-32 px-8 rounded-xl mt-2">
                  <textarea v-model="note"
                    class="bg-white resize-none w-full h-full outline-none text-sm border p-4 rounded-lg"
                    placeholder="Enter your message here or leave this field blank."></textarea>
                </div>
                <div class="flex justify-end mt-4 mr-8">
                  <div @click.stop="sendEmail"
                    class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">
                    <font-awesome-icon :icon="['fas', 'paper-plane']" />
                    <span class="ml-2">Send Email</span>
                  </div>
                </div>
              </div>
            </base-modal>
          </transition>

        </section>
      </div>
    </div>
    <div class="h-full flex justify-center items-center" v-if="isLoading || isExportExcel">
      <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
  </div>
</template>

<script>
import IconEye from "../../../components/icons/IconEye.vue";
import IconPen from "../../../components/icons/IconPen.vue";
import IconTrash from "../../../components/icons/IconTrash.vue";
import IconPlus from "../../../components/icons/IconPlus.vue";
import IconEnvelope from "../../../components/icons/IconEnvelope.vue";
import IconDocumentList from "../../../components/icons/IconDocumentList.vue";
import gql from "graphql-tag";
import Pagination from "../../../components/shared/pagination.vue";
import TableShimmer from "../../../components/shared/TableShimmer.vue";
import Create from "./create.vue";
import Edit from "./edit.vue";
import ApplyJob from "./job-opportunities/apply-job.vue";
import JobAcceptedStudents from "./job-accepted-students.vue";
import Search from "../../../components/shared/search.vue";
import RsdCopyToClipboard from "../../../components/shared/RsdCopyToClipboard.vue";
import xlsx from "xlsx/dist/xlsx.full.min.js";
export default {
  name: "jobs-index",
  components: {
    IconEye,
    IconPen,
    IconTrash,
    IconPlus,
    IconEnvelope,
    IconDocumentList,
    Pagination,
    TableShimmer,
    Create,
    Edit,
    ApplyJob,
    JobAcceptedStudents,
    Search,
    RsdCopyToClipboard,
  },
  data() {
    return {
      jobs: [],
      first: 10,
      page: 1,
      createModal: false,
      editModal: false,
      isViewItem: false,
      modalTitle: "",
      selectedItem: null,
      error: null,
      isShowJobAcceptedStudents: false,
      selectedJobId: null,
      isApproved: null,
      filterByEmployerType: "",
      filterByPartner: "",
      filterBySector: "",
      partnersList: [],
      nonRegisteredEmployersList: [],
      jobReport: null,
      filterByEntity: "",
      entities: [],
      chartOptions: {
        chart: {
          width: 380,
          type: "donut",
        },
        labels: ["Approved", "Not Verified", "Rejected"],
        plotOptions: {
          pie: {
            startAngle: -90,
            endAngle: 270,
          },
        },
        dataLabels: {
          enabled: true,
        },
        fill: {
          type: "gradient",
        },
        colors: ["#00e397", "#feb219", "#ff4561"],
        legend: {
          position: "bottom",
        },
      },
      filterByJobFor: "",
      filterByJobApplications: "",
      isLoading: false,
      searchText: "",
      showAddNote: false,
      selectedId: null,
      note: "",
      isExportExcel: false,
      isApiCallRecurisive: false,
      generatedReport: [],
    };
  },
  watch: {
    searchText() {
      this.page = 1;
    },
    jobs(newVal) {
      if (newVal.error) {
        if (newVal.error_code == 403) {
          this.$router.replace("/no-permission");
        } else if (newVal.error_code == 401) {
          this.$router.replace("/");
        } else {
          alert(newVal.message);
          this.$router.replace("/");
        }
      }
    },
    filterByEmployerType() {
      this.page = 1;
      this.getJobReport();
    },
    filterByPartner() {
      this.page = 1;
      this.getJobReport();
    },
    filterBySector() {
      this.page = 1;
      this.getJobReport();
    },
    filterByEntity() {
      this.filterByEmployerType = "";
      this.filterByPartner = "";
      this.filterBySector = "";
      this.page = 1;
      this.getPartnersList();
      this.getJobReport();
    },
    filterByJobFor() {
      this.page = 1;
      this.getJobReport();
    },
    filterByJobApplications() {
      this.page = 1;
      this.getJobReport();
    },
  },
  computed: {
    items() {
      return this.jobs.data;
    },
    hasItems() {
      if (this.items && this.items.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    paginatorInfo() {
      return this.jobs.paginatorInfo;
    },
    userId() {
      return this.$store.getters.userId;
    },
    role() {
      return this.$store.getters.role;
    },
    filteredEntities() {
      return this.entities.data;
    },
    series() {
      let approvedCount = 0;
      let rejectedCount = 0;
      let notVerifiedYetCount = 0;

      if (this.jobReport) {
        approvedCount = this.jobReport.approved_count;
        rejectedCount = this.jobReport.rejected_count;
        notVerifiedYetCount = this.jobReport.not_verified_yet_count;
      }

      return [approvedCount, notVerifiedYetCount, rejectedCount];
    },
    isShowFilters() {
      return ['admin', 'superadmin', 'Viewer', 'careercounselor'].includes(this.role);
    }
  },
  created() {
    if (this.role == "admin" || this.role == "superadmin" || this.role == "careercounselor") {
      this.getPartnersList();
      this.getNonRegisteredEmployersList();
      this.getJobReport();
    }
  },
  apollo: {
    jobs() {
      return {
        query: gql`
          query GetAllJobs(
            $first: Int!
            $page: Int!
            $filter_by_employer_type: String
            $filter_by_partner: Int
            $entity: String
            $job_for: String
            $entity_industry: String
            $search: String
            $request_count: String
          ) {
            jobs(
              first: $first
              page: $page
              filter_by_employer_type: $filter_by_employer_type
              filter_by_partner: $filter_by_partner
              entity: $entity
              job_for: $job_for
              entity_industry: $entity_industry
              search: $search
              request_count: $request_count
            ) {
              error
              error_code
              message
              data {
                id
                employer_type
                partner_id
                employer_name
                token
                grade_number
                job_title
                position_name
                position_number
                job_description
                minimum_qualification
                preferred_qualification
                expected_skills
                special_instructions
                division
                department
                job_start_date
                job_close_date
                job_category
                salary
                vacancy
                request_count
                approved_count
                rejected_count
                benefits
                job_location
                working_hours
                contract_type
                job_for
                student_status
                year_of_study
                program
                gender
                entity
                campus
                opportunity_type
                created_by_user_id
                is_posted
                partner {
                  id
                  en_company_name
                  ar_company_name
                  company_logo
                }
                created_by
                posted_by {
                  name
                }
              }
              paginatorInfo {
                count
                currentPage
                firstItem
                hasMorePages
                lastItem
                lastPage
                perPage
                total
              }
            }
          }
        `,
        variables() {
          return {
            first: this.first,
            page: this.page,
            filter_by_employer_type: this.filterByEmployerType,
            filter_by_partner: parseInt(this.filterByPartner),
            entity: this.filterByEntity,
            job_for: this.filterByJobFor,
            entity_industry: this.filterBySector,
            search: this.searchText,
            request_count: this.filterByJobApplications,
          };
        },
        fetchPolicy: "cache-and-network",
      };
    },
    entities() {
      return {
        query: gql`
          query GetEntityList {
            entities {
              data {
                entity
              }
            }
          }
        `,
        fetchPolicy: "cache-and-network",
      };
    },
  },
  methods: {
    onSearch(searchText) {
      this.searchText = searchText;
    },
    getEntityName(entity) {
      return entity.replace(/ATHS/g, "ATS");
    },
    formattedDate(date) {
      if (date) {
        const parsedDate = new Date(date);
        return parsedDate
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          .replace(/\//g, "-");
      } else {
        return "";
      }
    },
    setPage(page) {
      this.page = page;
    },
    closeModal() {
      this.createModal = false;
      this.editModal = false;
      this.isViewItem = false;
      this.modalTitle = "";
      this.selectedItem = null;
      this.isShowJobAcceptedStudents = false;
      this.selectedJobId = null;
      this.isApproved = null;
    },
    showToast(message = "") {
      this.$apollo.queries.jobs.refetch();
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "success",
        title: message,
      });
      this.closeModal();
    },
    onCreated() {
      this.showToast("Job Created Successfully");
    },
    editItem(item) {
      this.selectedItem = item;
      this.editModal = true;
    },
    async deleteItem(id) {
      this.$swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            const DELETE_MUTATION = gql`
              mutation DeleteJob($id: ID!) {
                deleteJob(id: $id) {
                  error
                  message
                }
              }
            `;

            try {
              await this.$apollo
                .mutate({
                  mutation: DELETE_MUTATION,
                  variables: {
                    id: id,
                  },
                })
                .then((data) => {
                  if (data.data.deleteJob.error) {
                    this.error = data.data.deleteJob.message;
                  }
                })
                .catch((error) => {
                  this.error = error.message;
                });
            } catch (error) {
              this.error = error.message || "Please try again.";
            }

            if (this.error === null) {
              this.showToast("Your job has been deleted.");
            } else {
              this.$swal
                .fire({
                  icon: "error",
                  title: "Oops...",
                  text: this.error,
                })
                .then((result) => {
                  if (result.value || result.dismiss) {
                    this.error = null;
                  }
                });
            }
          }
        });
    },
    onUpdated() {
      this.showToast("Job Updated Successfully");
    },
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      } else {
        const permissions = this.$store.getters.permissions;
        const permissionsArray = permissions.split(",");
        if (permissionsArray.includes(crudName)) {
          return true;
        } else {
          return false;
        }
      }
    },
    isEditableForMe(userId) {
      if (this.role == "superadmin" || this.role == 'partner') {
        return true;
      } else {
        if (this.userId == userId) {
          return true
        } else {
          return false;
        }
      }
    },
    showStudentsList(jobId, isApproved) {
      this.selectedJobId = jobId;
      this.isApproved = isApproved;
      this.isShowJobAcceptedStudents = true;
    },
    viewItem(item) {
      this.selectedItem = item;
      this.isViewItem = true;
    },
    getSrcFile(url) {
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    async getPartnersList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetActivePartnersList($entity: String) {
              activePartners(entity: $entity) {
                data {
                  id
                  en_company_name
                }
              }
            }
          `,
          variables: {
            entity: this.filterByEntity,
          },
          fetchPolicy: "network-only",
        });
        this.partnersList = data.activePartners.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getNonRegisteredEmployersList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetNonRegisteredEmployersList {
              getNonRegisteredEmployersList
            }
          `,
          fetchPolicy: "network-only",
        });
        this.nonRegisteredEmployersList = [...data.getNonRegisteredEmployersList, 'Other'];
      } catch (error) {
        console.log(error);
      }
    },
    async getJobReport() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetJobReport(
              $filter_by_employer_type: String
              $filter_by_partner: Int
              $entity: String
              $job_for: String
              $entity_industry: String
            ) {
              jobReport(
                filter_by_employer_type: $filter_by_employer_type
                filter_by_partner: $filter_by_partner
                entity: $entity
                job_for: $job_for
                entity_industry: $entity_industry
              ) {
                data {
                  total_vacancies
                  total_job_applications
                  not_verified_yet_count
                  approved_count
                  rejected_count
                }
              }
            }
          `,
          variables: {
            filter_by_employer_type: this.filterByEmployerType,
            filter_by_partner: parseInt(this.filterByPartner),
            entity: this.filterByEntity,
            job_for: this.filterByJobFor,
            entity_industry: this.filterBySector,
          },
          fetchPolicy: "network-only",
        });
        this.jobReport = data.jobReport.data;
      } catch (error) {
        console.log(error);
      }
    },
    openEmailContentModal(id) {
      this.selectedId = id;
      this.showAddNote = true;
    },
    closeAddNote() {
      this.showAddNote = false;
      this.note = "";
      this.selectedId = null;
    },
    async sendEmail() {
      this.showAddNote = false;
      this.isLoading = true;

      const SEND_EMAIL_MUTATION = gql`
        mutation JobSendEmail($id: ID!, $note: String) {
          jobSendEmail(id: $id, note: $note) {
            error
            message
          }
        }
      `;

      let error_msg = "";
      let success_msg = "";

      try {
        await this.$apollo
          .mutate({
            mutation: SEND_EMAIL_MUTATION,
            variables: {
              id: this.selectedId,
              note: this.note,
            },
          })
          .then((data) => {
            if (data.data.jobSendEmail.error) {
              error_msg = data.data.jobSendEmail.message;
            } else {
              success_msg = data.data.jobSendEmail.message;
            }
          })
          .catch((error) => {
            error_msg = error.message;
          });
      } catch (error) {
        error_msg = error.message || "Please try again.";
      }

      this.isLoading = false;

      if (error_msg == "") {
        this.$swal
          .fire({
            icon: "success",
            title: success_msg,
            text: "",
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              this.isLoading = false;
            }
          });
      } else {
        this.$swal
          .fire({
            icon: "error",
            title: "Oops...",
            text: error_msg,
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              this.isLoading = false;
            }
          });
      }
    },
    viewApplications(token) {
      this.$router.replace(`/employment/job-request-and-approval/${token}`);
    },
    getJobOpportunityUrl(token) {
      return process.env.VUE_APP_FRONTEND_URL + 'employment/job-opportunity/' + token;
    },
    async generateReport() {
      var page = 1;
      var limit = 200;
      this.isExportExcel = true;
      this.isApiCallRecurisive = true;
      while (this.isApiCallRecurisive) {
        const responseData = await this.fetchForExporting(page, limit);
        if (responseData.error) {
          this.$swal
            .fire({
              icon: "error",
              title: "Oops...",
              text: responseData.message,
            })
          break;
        }

        if (responseData.data.length > 0) {
          this.generatedReport.push(
            ...responseData.data.map((obj) => {
              return {
                employer: obj.employer_type == 'registered' ? obj.partner.en_company_name : obj.employer_name,
                entity: this.getEntityName(obj.entity),
                job_title: obj.job_title,
                position_name: obj.position_name,
                job_description: obj.job_description,
                job_location: obj.job_location,
                minimum_qualification: obj.minimum_qualification,
                preferred_qualification: obj.preferred_qualification,
                expected_skills: obj.expected_skills,
                special_instructions: obj.special_instructions,
                job_start_date: obj.job_start_date,
                job_close_date: obj.job_close_date,
                job_for: this.getJobForByNationality(obj.job_for),
                gender: this.getJobForByGender(obj.gender),
                salary: obj.salary,
                benefits: obj.benefits,
                contract_type: obj.contract_type,
                working_hours: obj.working_hours,
                department: obj.department,
                division: obj.division,
                job_category: obj.job_category,
                posted_by: obj.posted_by.name,
                vacancy: obj.vacancy,
                no_of_applications: obj.no_of_applications,
              };
            })
          );
          page++;
        } else {
          this.isApiCallRecurisive = false;
          break;
        }
      }

      const entityMap = {
        ADPOLY: "ADPOLY_JOBS",
        FCHS: "FCHS_JOBS",
        ATHS: "ATS_JOBS",
        ATS: "ATS_JOBS",
        ADVETI: "ADVETI_JOBS"
      };
      const reportName = entityMap[this.filterByEntity] || "JOBS";

      this.exportToExcel(reportName);
    },
    async fetchForExporting(pageNo = 1, limitSize = 50) {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query ExportJobs(
              $page: Int
              $limit: Int
              $filter_by_employer_type: String
              $filter_by_partner: Int
              $entity: String
              $job_for: String
              $entity_industry: String
              $search: String
              $request_count: String
            ) {
              exportJobs(
                page: $page
                limit: $limit
                filter_by_employer_type: $filter_by_employer_type
                filter_by_partner: $filter_by_partner
                entity: $entity
                job_for: $job_for
                entity_industry: $entity_industry
                search: $search
                request_count: $request_count
              ) {
                error
                error_code
                message
                data {
                  employer_type
                  employer_name
                  entity
                  job_title
                  position_name
                  job_description
                  job_location
                  minimum_qualification
                  preferred_qualification
                  expected_skills
                  special_instructions
                  job_start_date
                  job_close_date
                  job_for
                  gender
                  salary
                  benefits
                  contract_type
                  working_hours
                  department
                  division
                  job_category
                  vacancy
                  no_of_applications
                  posted_by {
                    name
                  }
                  partner {
                    en_company_name
                  }
                }
              }
            }
          `,
          fetchPolicy: "network-only",
          variables: {
            page: pageNo,
            limit: limitSize,
            filter_by_employer_type: this.filterByEmployerType,
            filter_by_partner: parseInt(this.filterByPartner),
            entity: this.filterByEntity,
            job_for: this.filterByJobFor,
            entity_industry: this.filterBySector,
            search: this.searchText,
            request_count: this.filterByJobApplications,
          },
        });

        const responseData = data.exportJobs;
        return responseData;
      } catch (error) {
        console.log(error.message);
      }
    },
    exportToExcel(reportName) {
      const XLSX = xlsx;

      let header = [
        "EMPLOYER",
        "ENTITY",
        "JOB TITLE",
        "POSITION",
        "JOB DESCRIPTION",
        "JOB LOCATION",
        "MINIMUM QUALIFICATION",
        "PREFERED QUALIFICATION",
        "EXPECTED SKILLS",
        "SPECIAL INSTRUCTIONS",
        "JOB OFFER START DATE",
        "JOB OFFER CLOSE DATE",
        "JOB FOR (BY NATIONALITY)",
        "JOB FOR (BY GENDER)",
        "SALARY",
        "BENEFITS",
        "CONTRACT TYPE",
        "WORKING HOURS",
        "DEPARTMENT",
        "DIVISION",
        "JOB CATEGORY",
        "JOB POSTED BY",
        "VACANCY",
        "NO OF APPLICATIONS",
      ];

      const worksheet = XLSX.utils.json_to_sheet(this.generatedReport);
      const columnWidths = [];

      // Dynamically set headers in the worksheet
      for (let i = 0; i < header.length; i++) {
        const column = String.fromCharCode(65 + (i % 26)); // Generate column letters (A-Z)
        const row = Math.floor(i / 26); // Determine if the column extends beyond Z (AA, AB, etc.)
        const columnLetter = row > 0 ? String.fromCharCode(64 + row) + column : column; // Handle multi-letter columns
        worksheet[`${columnLetter}1`] = { v: header[i], t: "s" }; // Assign header value

        // Set a default width dynamically based on the header text length
        columnWidths.push({ width: Math.max(40, header[i].length + 5) }); // Ensure a minimum width of 40
      }

      // Assign dynamic column widths to the worksheet
      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, `${reportName}.xlsx`);

      this.generatedReport = [];
      this.isExportExcel = false;
      this.isApiCallRecurisive = false;
    },
    getJobForByNationality(value) {
      const map = {
        1: "Local",
        2: "Non Local",
        3: "Both Local and Non Local"
      };
      return map[value] || "";
    },
    getJobForByGender(value) {
      const map = {
        'M': 'Male',
        'F': 'Female'
      };
      return map[value] || 'Both Male & Female';
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
